import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Modal, Skeleton, Alert } from 'antd'
import { DefaultJsonForm } from '../../../../components/v2/default-json-form'
import fetchAPI from '../../../../../lib/utils/fetch-api'
import { schema } from './schema'
import { objectToUrl, renameKeys, setEnumFields } from '../../../utils'
import { DataType } from '../list/interface'

interface PlanModalProps {
  visible: boolean
  record: DataType | null
  onCancel: () => void
  onOk: () => void
}

const PlanModal: React.FC<PlanModalProps> = ({ visible, record, onCancel, onOk }) => {
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false)
  const [pageErrors, setPageErrors] = useState<string[]>([])
  const formDataRef = useRef<any>({})
  const [matchedJSONSchema, setMatchedJSONSchema] = useState<any>(null)
  const [formData, setFormData] = useState<any>({})

  const savePlan = useCallback(async () => {
    try {
      const id = record?.id
      const url = id ? `/api/hr/plans/${id}` : '/api/hr/plans/new'
      const method = 'POST'

      await fetchAPI(url, {
        method,
        body: JSON.stringify(formData),
      }).then(result => {
        if (result?.error) {
          const errors =
            Array.isArray(result['errors']) && result['errors']?.length > 0
              ? result['errors'].map(error => error['detail'])
              : ['Неизвестная ошибка при выполнении запроса']
          setPageErrors(errors)
        } else {
          onOk()
        }
      })
    } catch (error) {
      console.error('Ошибка при сохранении плана:', error)
      setPageErrors(['Ошибка при сохранении плана'])
    }
  }, [record, formData, onOk])

  const getData = useCallback(async () => {
    if (!visible) return

    setIsDataLoading(true)
    setPageErrors([])

    try {
      if (record?.id) {
        // При редактировании используем данные из таблицы
        formDataRef.current = {
          date_start: record.date_start,
          date_end: record.date_end,
          quantity: record.quantity,
          vacancy_id: record.vacancy_id,
          department_id: record.department_id,
          status: record.status,
        }
      } else {
        // При создании новой записи запрашиваем шаблон
        const data = await fetchAPI(`/api/hr/plans/new`)
        const formData = data.reduce((obj, cur) => ({ ...obj, [cur.name]: cur.value }), {})
        formDataRef.current = { ...formData }
      }

      // Всегда загружаем enum'ы для выпадающих списков
      const enumsList = { types: ['hr_vacancy', 'hr_department'] }
      const enums = await fetchAPI(`/api/enum-list${objectToUrl(enumsList)}`)
      const fixKeys = renameKeys(enums, {
        hr_vacancy: 'vacancy_id',
        hr_department: 'department_id',
      })

      const schemaCopy = JSON.parse(JSON.stringify(schema))
      setEnumFields(fixKeys, schemaCopy)
      setMatchedJSONSchema(schemaCopy)
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error)
      setPageErrors(['Ошибка при загрузке данных'])
    } finally {
      setIsDataLoading(false)
    }
  }, [visible, record])

  useEffect(() => {
    getData()
  }, [getData])

  const onFormData = useCallback(
    ({ formData }) => {
      setFormData(formData)
    },
    [setFormData],
  )

  const handleOk = useCallback(async () => {
    if (!formData || Object.keys(formData).length === 0) {
      setPageErrors(['Заполните все обязательные поля'])
      return
    }
    await savePlan()
  }, [formData, savePlan])

  return (
    <Modal
      title={record?.id ? 'Редактировать план' : 'Добавить план'}
      open={visible}
      onCancel={() => {
        setPageErrors([])
        setFormData({})
        formDataRef.current = {}
        onCancel()
      }}
      onOk={handleOk}
      okText="Сохранить"
      cancelText="Отмена"
      width={600}
      destroyOnHidden={true}
    >
      {pageErrors.length > 0 ? (
        <Alert
          message="При выполнении операции возникла ошибка:"
          showIcon
          type="error"
          description={pageErrors.join('. ')}
          style={{ marginBottom: 16 }}
        />
      ) : null}
      {isDataLoading ? (
        <Skeleton active />
      ) : matchedJSONSchema ? (
        <DefaultJsonForm
          formData={formDataRef.current}
          schema={matchedJSONSchema}
          onChange={onFormData}
          orientation="vertical"
        />
      ) : null}
    </Modal>
  )
}

export default PlanModal

